import React from 'react'

import { Rule } from 'antd/lib/form'

import { ProjectLocationCaptureBlockFormValue } from '../../templateEditor/BlockConfigs/ProjectLocationCaptureBlockConfig'
import { ProjectLocationCaptureBlock as ProjectLocationCaptureBlockType } from '../../types/blueprint'

import { CaptureBlockFormItem } from './CaptureBlockFormItem'

import {
  ProjectLocationFormDropdown,
  ProjectLocationFormDropdownProps,
} from '@/components/form-components/dropdowns/ProjectLocation/ProjectLocationFormDropdown'
import { useProject } from '@/context/projectContext'
import { useTemplateRenderer } from '@/context/templateRendererContext'

type ProjectLocationFormWrapperProps = {
  projectId: string
  value?: ProjectLocationCaptureBlockFormValue
  onChange?: (value: ProjectLocationCaptureBlockFormValue) => void
}

/**
 * Convert the format that is stored in the template to the format that is used in the form item
 */
const ProjectLocationFormWrapper: React.FC<ProjectLocationFormWrapperProps> = ({
  projectId,
  value,
  onChange,
}) => {
  const onLocationChange: ProjectLocationFormDropdownProps['onChange'] = (
    newValue
  ) => {
    onChange?.({
      id: newValue?.id,
      projectLocationName: newValue?.projectLocationName,
    })
  }

  return (
    <ProjectLocationFormDropdown
      projectId={projectId}
      showCaret={true}
      onChange={onLocationChange}
      value={value}
    />
  )
}

type Props = {
  block: ProjectLocationCaptureBlockType
  rules?: Rule[]
}

export const ProjectLocationCaptureBlock: React.FC<Props> = ({
  block,
  rules,
}) => {
  const { projectId, renderOptions } = useTemplateRenderer()
  const projectContext = useProject()
  const project = projectContext?.project

  // Determine effective project ID: use template context first, fallback to global project in preview
  const effectiveProjectId =
    projectId ||
    (renderOptions.isPreview && project?.id !== 'builder' ? project?.id : null)

  if (!effectiveProjectId) {
    // In preview mode with no project selected, show a helpful placeholder
    if (renderOptions.isPreview) {
      return (
        <CaptureBlockFormItem
          block={block}
          rules={rules}
          label={block.name || 'Project Location'}
          initialValue={null}
        >
          <div
            style={{
              padding: '8px 12px',
              backgroundColor: '#f8f9fa',
              border: '2px dashed #dee2e6',
              borderRadius: '6px',
              color: '#6c757d',
              fontStyle: 'italic',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span>Project Location (Preview Mode)</span>
            <span style={{ fontSize: '12px', opacity: 0.7 }}>
              Select a project to see locations
            </span>
          </div>
        </CaptureBlockFormItem>
      )
    }
    return null
  }

  const { value, projectLocationName } = block

  return (
    <CaptureBlockFormItem
      block={block}
      rules={rules}
      initialValue={
        value
          ? ({
              id: value,
              projectLocationName,
            } as ProjectLocationCaptureBlockFormValue)
          : null
      }
    >
      <ProjectLocationFormWrapper projectId={effectiveProjectId} />
    </CaptureBlockFormItem>
  )
}
